<script setup lang="ts">
import { addDays, addWeeks, isBefore, parseISO, startOfDay } from "date-fns";
import Button from "primevue/button";
import Skeleton from "primevue/skeleton";
import { computed, ref, toRaw } from "vue";
import { useToast } from "vue-toast-notification";
import CreateEditTodosModal from "@/components/modals/todos/CreateEditTodosModal.vue";
import type {
  SessionTodoForm,
  WysiwygContent,
} from "@/components/program-manager/sessions/components/TodosSection/types";
import { hasTodoAddPermission } from "@/composables/authorization.js";
import { useTenantTheming } from "@/composables/useTenantTheming";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "@/constants.js";
import { useApplicationTodo } from "@/providers/applicationTodos/useApplicationTodo";
import { addButtonDisabledPt, addButtonPt } from "./styles";
import TodoRow from "./TodoRow.vue";
import type { FilterableTodosSectionProps, StatusGroupKey, StatusTileConfig, TileKey, TodoSubmission } from "./types";
import { STATUS_GROUP_MAP, STATUS_TILES } from "./types";
import { todoSubmissionToForm } from "./utils";

type GroupByMode = "status" | "dueDate" | "priority";

interface GenericGroup {
  key: string;
  label: string;
  todos: TodoSubmission[];
}

const props = withDefaults(defineProps<FilterableTodosSectionProps>(), {
  loading: false,
  canViewSubmissions: false,
  studentOktaId: undefined,
  applicationId: undefined,
});

const toast = useToast();

const { secondaryColorClassButton } = useTenantTheming();

const addButtonPtBinding = computed(() => addButtonPt(secondaryColorClassButton.value));

const newTodoInitialState = (): SessionTodoForm => ({
  todo_id: null,
  required: true,
  placement: "Onboarding",
  ranking: 2,
  display_name: null,
  due_date: null,
  instructions: {} as WysiwygContent,
  supporting_url: null,
});

const modalOpen = ref<boolean>(false);
const isEditingTodo = ref<boolean>(false);
const activeTodo = ref<SessionTodoForm>(newTodoInitialState());
const activeTodoTitle = ref<string>("");

const canAddTodo = computed<boolean>(() => hasTodoAddPermission.value && Boolean(props.applicationId));

const { upsert: applicationTodoUpsert, archive: applicationTodoArchive } = useApplicationTodo();
const isSavingTodo = computed<boolean>(
  () => applicationTodoUpsert.isPending.value || applicationTodoArchive.isPending.value,
);

const assignedTodos = computed<SessionTodoForm[]>(() =>
  props.todos.filter((todo) => !todo.archived_at).map(todoSubmissionToForm),
);

const openAddTodoModal = (): void => {
  if (!canAddTodo.value) return;
  activeTodo.value = newTodoInitialState();
  isEditingTodo.value = false;
  modalOpen.value = true;
};

const handleEditTodo = (todo: TodoSubmission): void => {
  activeTodo.value = todoSubmissionToForm(structuredClone(toRaw(todo)));
  activeTodoTitle.value = todo.title;
  isEditingTodo.value = true;
  modalOpen.value = true;
};

const closeModal = (): void => {
  modalOpen.value = false;
  isEditingTodo.value = false;
  activeTodo.value = newTodoInitialState();
  activeTodoTitle.value = "";
};

const handleSaveTodo = async (): Promise<void> => {
  if (!props.applicationId || !activeTodo.value.todo_id) return;
  const wasEditing = isEditingTodo.value;
  try {
    await applicationTodoUpsert.mutateAsync({
      applicationId: props.applicationId,
      todoId: activeTodo.value.todo_id,
      participantId: props.studentOktaId,
      payload: {
        display_name: activeTodo.value.display_name,
        placement: activeTodo.value.placement,
        supporting_url: activeTodo.value.supporting_url,
        instructions: (activeTodo.value.instructions as Record<string, unknown> | undefined) ?? null,
        ranking: activeTodo.value.ranking,
        due_date: activeTodo.value.due_date,
        required: activeTodo.value.required,
      },
    });
    toast.open({
      message: wasEditing ? "To-Do updated successfully." : "To-Do added successfully.",
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
    closeModal();
  } catch (error) {
    toast.open({
      message: wasEditing
        ? "Error while updating the To-Do. Please try again."
        : "Error while adding the To-Do. Please try again.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    throw error;
  }
};

const handleRemoveTodo = async (): Promise<void> => {
  const todoId = activeTodo.value.todo_id;
  if (!props.applicationId || !todoId) return;
  try {
    await applicationTodoArchive.mutateAsync({
      applicationId: props.applicationId,
      todoId,
      participantId: props.studentOktaId,
    });
    toast.open({
      message: "To-Do removed successfully.",
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  } catch {
    toast.open({
      message: "Error while removing the To-Do. Please try again.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};

const groupedTodos = computed<Record<StatusGroupKey, TodoSubmission[]>>(() => {
  const groups: Record<StatusGroupKey, TodoSubmission[]> = {
    notAccepted: [],
    notSubmitted: [],
    pending: [],
    completed: [],
    archived: [],
  };

  for (const todo of props.todos) {
    if (todo.archived_at) {
      groups.archived.push(todo);
      continue;
    }
    const group = STATUS_GROUP_MAP[todo.status];
    if (group) {
      groups[group].push(todo);
    }
  }

  const sortByDueDate = (a: TodoSubmission, b: TodoSubmission): number => {
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  };

  for (const key of Object.keys(groups) as StatusGroupKey[]) {
    groups[key].sort(sortByDueDate);
  }

  return groups;
});

const tileCounts = computed<Record<"notAccepted" | "notSubmitted" | "pending" | "completed", number>>(() => ({
  notAccepted: groupedTodos.value.notAccepted.length,
  notSubmitted: groupedTodos.value.notSubmitted.length,
  pending: groupedTodos.value.pending.length,
  completed: groupedTodos.value.completed.length,
}));

const totalCount = computed<number>(() => props.todos.length);

const hasNoToDos = computed<boolean>(() => !props.loading && totalCount.value === 0);

const activeFilter = ref<TileKey | undefined>(undefined);

const activeFilterTile = computed<StatusTileConfig | undefined>(() =>
  activeFilter.value ? STATUS_TILES.find((t) => t.key === activeFilter.value) : undefined,
);

// pca = Pending, Completed, & Archived
// These groups are combined and collapsed on initial load
const pcaExpandedBeforeFilter = ref<boolean>(false);

const toggleFilter = (key: TileKey): void => {
  const wasFilteringPca = activeFilter.value === "pending" || activeFilter.value === "completed";
  activeFilter.value = activeFilter.value === key ? undefined : key;
  const isFilteringPca = activeFilter.value === "pending" || activeFilter.value === "completed";

  if (isFilteringPca && !wasFilteringPca) {
    pcaExpandedBeforeFilter.value = pcaExpanded.value;
    pcaExpanded.value = true;
  } else if (!isFilteringPca && wasFilteringPca) {
    pcaExpanded.value = pcaExpandedBeforeFilter.value;
  }
};

const clearFilter = (): void => {
  const wasFilteringPca = activeFilter.value === "pending" || activeFilter.value === "completed";
  activeFilter.value = undefined;
  if (wasFilteringPca) {
    pcaExpanded.value = pcaExpandedBeforeFilter.value;
  }
};

const showNotAccepted = computed<boolean>(() => {
  if (activeFilter.value) return activeFilter.value === "notAccepted";
  return groupedTodos.value.notAccepted.length > 0;
});

const showNotSubmitted = computed<boolean>(() => {
  if (activeFilter.value) return activeFilter.value === "notSubmitted";
  return groupedTodos.value.notSubmitted.length > 0;
});

const showFilteredPending = computed<boolean>(
  () => activeFilter.value === "pending" && groupedTodos.value.pending.length > 0,
);

const showFilteredCompleted = computed<boolean>(
  () => activeFilter.value === "completed" && groupedTodos.value.completed.length > 0,
);

const pcaCount = computed<number>(
  () => tileCounts.value.pending + tileCounts.value.completed + groupedTodos.value.archived.length,
);

const showPca = computed<boolean>(() => {
  if (activeFilter.value === "pending" || activeFilter.value === "completed") return false;
  return pcaCount.value > 0;
});

const pcaExpanded = ref<boolean>(false);
const expandedRowIds = ref<Set<string>>(new Set());

const togglePca = (): void => {
  pcaExpanded.value = !pcaExpanded.value;
};

const toggleRow = (todoId: string): void => {
  const newSet = new Set(expandedRowIds.value);
  if (newSet.has(todoId)) {
    newSet.delete(todoId);
  } else {
    newSet.add(todoId);
  }
  expandedRowIds.value = newSet;
};

const isRowExpanded = (todoId: string): boolean => expandedRowIds.value.has(todoId);

const isOverdue = (todo: TodoSubmission): boolean => {
  if (todo.status === "Completed" || !todo.due_date) return false;
  const today = startOfDay(new Date());
  const dueDate = startOfDay(parseISO(todo.due_date));
  return isBefore(dueDate, today);
};

const isHighPriority = (todo: TodoSubmission): boolean => todo.ranking === 1;

const groupByMode = ref<GroupByMode>("status");

const GROUP_BY_OPTIONS: { key: GroupByMode; label: string }[] = [
  { key: "status", label: "Status" },
  { key: "dueDate", label: "Due date" },
  { key: "priority", label: "Priority" },
];

const sortByDueDate = (a: TodoSubmission, b: TodoSubmission): number => {
  if (!a.due_date && !b.due_date) return 0;
  if (!a.due_date) return 1;
  if (!b.due_date) return -1;
  return a.due_date.localeCompare(b.due_date);
};

const dueDateGroups = computed<GenericGroup[]>(() => {
  const nonArchived = props.todos.filter((t) => !t.archived_at);
  const filtered = activeFilter.value
    ? nonArchived.filter((t) => STATUS_GROUP_MAP[t.status] === activeFilter.value)
    : nonArchived;

  const today = startOfDay(new Date());
  const oneWeekOut = addDays(today, 7);
  const threeWeeksOut = addWeeks(today, 3);

  const overdue: TodoSubmission[] = [];
  const thisWeek: TodoSubmission[] = [];
  const soon: TodoSubmission[] = [];
  const later: TodoSubmission[] = [];
  const noDueDate: TodoSubmission[] = [];

  for (const todo of filtered) {
    if (!todo.due_date) {
      noDueDate.push(todo);
    } else {
      const due = startOfDay(parseISO(todo.due_date));
      if (isBefore(due, today)) {
        overdue.push(todo);
      } else if (isBefore(due, oneWeekOut)) {
        thisWeek.push(todo);
      } else if (isBefore(due, threeWeeksOut)) {
        soon.push(todo);
      } else {
        later.push(todo);
      }
    }
  }

  const groups: GenericGroup[] = [];
  if (overdue.length > 0) groups.push({ key: "overdue", label: "Overdue", todos: overdue.sort(sortByDueDate) });
  if (thisWeek.length > 0)
    groups.push({ key: "thisweek", label: "Due this week", todos: thisWeek.sort(sortByDueDate) });
  if (soon.length > 0) groups.push({ key: "soon", label: "Due in 2\u20133 weeks", todos: soon.sort(sortByDueDate) });
  if (later.length > 0) groups.push({ key: "later", label: "Later", todos: later.sort(sortByDueDate) });
  if (noDueDate.length > 0) groups.push({ key: "none", label: "No due date", todos: noDueDate });
  return groups;
});

const priorityGroups = computed<GenericGroup[]>(() => {
  const nonArchived = props.todos.filter((t) => !t.archived_at);
  const filtered = activeFilter.value
    ? nonArchived.filter((t) => STATUS_GROUP_MAP[t.status] === activeFilter.value)
    : nonArchived;

  const high: TodoSubmission[] = [];
  const normal: TodoSubmission[] = [];

  for (const todo of filtered) {
    if (todo.ranking === 1) {
      high.push(todo);
    } else {
      normal.push(todo);
    }
  }

  const groups: GenericGroup[] = [];
  if (high.length > 0) {
    groups.push({ key: "high", label: "High Priority", todos: high.sort(sortByDueDate) });
  }
  if (normal.length > 0) {
    groups.push({ key: "normal", label: "Normal Priority", todos: normal.sort(sortByDueDate) });
  }
  return groups;
});
</script>

<template>
  <div class="w-full">
    <div class="flex items-center justify-between mb-4 gap-4 px-6">
      <span class="text-base font-semibold text-indigo-base whitespace-nowrap">
        To-Dos ({{ loading ? "..." : totalCount }})
      </span>
      <div class="flex items-center gap-3 ml-auto">
        <div v-if="!hasNoToDos" class="flex items-center gap-2">
          <span class="text-xs text-gray-600 font-medium">Group by</span>
          <div class="inline-flex rounded-lg bg-gray-10 p-0.5 gap-0.5">
            <button
              v-for="opt in GROUP_BY_OPTIONS"
              :key="opt.key"
              class="px-3 py-1.5 text-xs font-semibold rounded-md transition-all"
              :class="
                groupByMode === opt.key
                  ? 'bg-white text-indigo-base shadow-sm'
                  : 'text-gray-600 hover:text-indigo-base'
              "
              @click="groupByMode = opt.key"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
        <Button
          :pt="canAddTodo ? addButtonPtBinding : addButtonDisabledPt"
          :disabled="!canAddTodo || isSavingTodo"
          :aria-label="canAddTodo ? 'Add to-do' : 'Add to-do (permission required)'"
          data-testid="add-todo-button"
          @click="openAddTodoModal"
        >
          <span class="material-symbols-outlined text-sm leading-none mr-1">add</span>
          <span>ADD TO-DO</span>
        </Button>
      </div>
    </div>

    <div v-if="hasNoToDos" class="px-6 pb-4 text-sm text-gray-600" data-testid="todos-empty-state">
      No to-dos yet.
    </div>

    <div v-else class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 px-6">
      <template v-if="loading">
        <Skeleton
          v-for="i in 4"
          :key="i"
          height="4rem"
          border-radius="0.5rem"
        />
      </template>
      <template v-else>
        <button
          v-for="tile in STATUS_TILES"
          :key="tile.key"
          class="flex items-stretch rounded-lg border text-left transition-all overflow-hidden"
          :class="[
            activeFilter === tile.key
              ? 'bg-gray-100 border-indigo-base'
              : 'bg-white border-gray-175 hover:bg-gray-50',
          ]"
          @click="toggleFilter(tile.key)"
        >
          <div class="w-1 flex-shrink-0 rounded mx-4 my-3" :class="tile.barClass" />
          <div class="flex flex-col justify-center pe-3 py-3">
            <span class="text-2xl font-bold text-indigo-base leading-tight">{{ tileCounts[tile.key] }}</span>
            <span class="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">{{ tile.label }}</span>
          </div>
        </button>
      </template>
    </div>

    <div v-if="activeFilterTile" class="flex items-center gap-2 mb-4 px-6">
      <span
        class="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white pl-3 pr-1.5 py-1 text-xs font-semibold text-indigo-base"
      >
        {{ activeFilterTile.label }}
        <span class="bg-indigo-base text-white rounded-full min-w-5 h-5 inline-flex items-center justify-center px-1.5 text-[11px] font-bold">
          {{ tileCounts[activeFilterTile.key] }}
        </span>
      </span>
      <button
        class="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
        @click="clearFilter"
      >
        Clear
        <span class="material-symbols-outlined text-xs leading-none">close</span>
      </button>
    </div>

    <template v-if="loading">
      <div class="space-y-3 px-6">
        <Skeleton width="40%" height="1rem" />
        <Skeleton v-for="i in 3" :key="i" height="3.5rem" border-radius="0.375rem" />
      </div>
    </template>

    <template v-else>
      <template v-if="groupByMode === 'status'">
        <div v-if="showNotAccepted" class="mb-4">
          <div class="flex items-center gap-2 px-6 py-3 border-t border-gray-200 bg-blue-100">
            <span class="text-sm font-bold text-indigo-base">Not Accepted</span>
            <span class="text-sm text-gray-600">{{ tileCounts.notAccepted }}</span>
          </div>
          <div class="divide-y divide-gray-200">
            <TodoRow
              v-for="todo in groupedTodos.notAccepted"
              :key="todo.todo_id"
              :todo="todo"
              :expanded="isRowExpanded(todo.todo_id)"
              :overdue="isOverdue(todo)"
              :high-priority="isHighPriority(todo)"
              :can-view-submissions="canViewSubmissions"
              @toggle="toggleRow(todo.todo_id)"
              @edit="handleEditTodo(todo)"
            />
          </div>
        </div>

        <div v-if="showNotSubmitted" class="mb-4">
          <div class="flex items-center gap-2 px-6 py-3 border-t border-gray-200 bg-blue-100">
            <span class="text-sm font-bold text-indigo-base">Not Submitted</span>
            <span class="text-sm text-gray-600">{{ tileCounts.notSubmitted }}</span>
          </div>
          <div class="divide-y divide-gray-200">
            <TodoRow
              v-for="todo in groupedTodos.notSubmitted"
              :key="todo.todo_id"
              :todo="todo"
              :expanded="isRowExpanded(todo.todo_id)"
              :overdue="isOverdue(todo)"
              :high-priority="isHighPriority(todo)"
              :can-view-submissions="canViewSubmissions"
              @toggle="toggleRow(todo.todo_id)"
              @edit="handleEditTodo(todo)"
            />
          </div>
        </div>

        <div v-if="showFilteredPending" class="mb-4">
          <div class="flex items-center gap-2 px-6 py-3 border-t border-gray-200 bg-blue-100">
            <span class="text-sm font-bold text-indigo-base">Pending</span>
            <span class="text-sm text-gray-600">{{ tileCounts.pending }}</span>
          </div>
          <div class="divide-y divide-gray-200">
            <TodoRow
              v-for="todo in groupedTodos.pending"
              :key="todo.todo_id"
              :todo="todo"
              :expanded="isRowExpanded(todo.todo_id)"
              :overdue="isOverdue(todo)"
              :high-priority="isHighPriority(todo)"
              :can-view-submissions="canViewSubmissions"
              @toggle="toggleRow(todo.todo_id)"
              @edit="handleEditTodo(todo)"
            />
          </div>
        </div>

        <div v-if="showFilteredCompleted" class="mb-4">
          <div class="flex items-center gap-2 px-6 py-3 border-t border-gray-200 bg-blue-100">
            <span class="text-sm font-bold text-indigo-base">Completed</span>
            <span class="text-sm text-gray-600">{{ tileCounts.completed }}</span>
          </div>
          <div class="divide-y divide-gray-200">
            <TodoRow
              v-for="todo in groupedTodos.completed"
              :key="todo.todo_id"
              :todo="todo"
              :expanded="isRowExpanded(todo.todo_id)"
              :overdue="isOverdue(todo)"
              :high-priority="isHighPriority(todo)"
              :can-view-submissions="canViewSubmissions"
              @toggle="toggleRow(todo.todo_id)"
              @edit="handleEditTodo(todo)"
            />
          </div>
        </div>

        <div v-if="showPca" class="mb-4 border-gray-200 border-y">
          <button
            class="flex w-full items-center gap-2 py-3 px-6 text-left hover:bg-gray-50 transition-colors"
            @click="togglePca"
          >
            <span
              class="material-symbols-outlined text-base text-gray-600 transition-transform duration-150"
              :class="{ 'rotate-90': pcaExpanded }"
            >
              chevron_right
            </span>
            <span class="text-sm font-bold text-indigo-base">Pending, Completed &amp; Archived</span>
            <span class="text-sm text-gray-600">({{ pcaCount }})</span>
          </button>

          <Transition name="slide">
            <div v-if="pcaExpanded">
              <div v-if="groupedTodos.pending.length > 0" class="mb-3">
                <div class="flex items-center gap-2 py-1.5 px-6">
                  <span class="text-xs font-semibold text-gray-600 uppercase tracking-wider">Pending</span>
                  <span class="text-xs text-gray-600">{{ tileCounts.pending }}</span>
                </div>
                <div class="divide-y divide-gray-200">
                  <TodoRow
                    v-for="todo in groupedTodos.pending"
                    :key="todo.todo_id"
                    :todo="todo"
                    :expanded="isRowExpanded(todo.todo_id)"
                    :overdue="isOverdue(todo)"
                    :high-priority="isHighPriority(todo)"
                    :can-view-submissions="canViewSubmissions"
                    @toggle="toggleRow(todo.todo_id)"
                    @edit="handleEditTodo(todo)"
                  />
                </div>
              </div>

              <div v-if="groupedTodos.completed.length > 0" class="mb-3">
                <div class="flex items-center gap-2 py-1.5 px-6">
                  <span class="text-xs font-semibold text-gray-600 uppercase tracking-wider">Completed</span>
                  <span class="text-xs text-gray-600">{{ tileCounts.completed }}</span>
                </div>
                <div class="divide-y divide-gray-200">
                  <TodoRow
                    v-for="todo in groupedTodos.completed"
                    :key="todo.todo_id"
                    :todo="todo"
                    :expanded="isRowExpanded(todo.todo_id)"
                    :overdue="isOverdue(todo)"
                    :high-priority="isHighPriority(todo)"
                    :can-view-submissions="canViewSubmissions"
                    @toggle="toggleRow(todo.todo_id)"
                    @edit="handleEditTodo(todo)"
                  />
                </div>
              </div>

              <div v-if="groupedTodos.archived.length > 0" class="mb-3">
                <div class="flex items-center gap-2 py-1.5 px-6">
                  <span class="text-xs font-semibold text-gray-600 uppercase tracking-wider">Archived</span>
                  <span class="text-xs text-gray-600">{{ groupedTodos.archived.length }}</span>
                </div>
                <div class="divide-y divide-gray-200">
                  <TodoRow
                    v-for="todo in groupedTodos.archived"
                    :key="todo.todo_id"
                    :todo="todo"
                    :expanded="isRowExpanded(todo.todo_id)"
                    :overdue="isOverdue(todo)"
                    :high-priority="isHighPriority(todo)"
                    :can-view-submissions="canViewSubmissions"
                    @toggle="toggleRow(todo.todo_id)"
                    @edit="handleEditTodo(todo)"
                  />
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </template>

      <template v-else-if="groupByMode === 'dueDate'">
        <div v-for="group in dueDateGroups" :key="group.key" class="mb-4">
          <div class="flex items-center gap-2 px-6 py-3 border-t border-gray-200 bg-blue-100">
            <span class="text-sm font-bold text-indigo-base">{{ group.label }}</span>
            <span class="text-sm text-gray-600">{{ group.todos.length }}</span>
          </div>
          <div class="divide-y divide-gray-200">
            <TodoRow
              v-for="todo in group.todos"
              :key="todo.todo_id"
              :todo="todo"
              :expanded="isRowExpanded(todo.todo_id)"
              :overdue="isOverdue(todo)"
              :high-priority="isHighPriority(todo)"
              :can-view-submissions="canViewSubmissions"
              @toggle="toggleRow(todo.todo_id)"
              @edit="handleEditTodo(todo)"
            />
          </div>
        </div>
      </template>

      <template v-else-if="groupByMode === 'priority'">
        <div v-for="group in priorityGroups" :key="group.key" class="mb-4">
          <div class="flex items-center gap-2 px-6 py-3 border-t border-gray-200 bg-blue-100">
            <span class="text-sm font-bold text-indigo-base">{{ group.label }}</span>
            <span class="text-sm text-gray-600">{{ group.todos.length }}</span>
          </div>
          <div class="divide-y divide-gray-200">
            <TodoRow
              v-for="todo in group.todos"
              :key="todo.todo_id"
              :todo="todo"
              :expanded="isRowExpanded(todo.todo_id)"
              :overdue="isOverdue(todo)"
              :high-priority="isHighPriority(todo)"
              :can-view-submissions="canViewSubmissions"
              @toggle="toggleRow(todo.todo_id)"
              @edit="handleEditTodo(todo)"
            />
          </div>
        </div>
      </template>
    </template>

    <CreateEditTodosModal
      data-testid="create-application-todo-modal"
      :open="modalOpen"
      :model-value="activeTodo"
      :edit="isEditingTodo"
      :fallback-title="activeTodoTitle"
      :saving="isSavingTodo"
      :close-on-save="false"
      :current-todos="assignedTodos"
      @close-modal="closeModal"
      @save-todo="handleSaveTodo"
      @remove-todo="handleRemoveTodo"
    />
  </div>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
  max-height: 2000px;
}
.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
